// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G12 Tariff Calculator (Two-Rate Day/Night)
 *
 * @description Calculates costs for G12 day/night tariff
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G12Pricing, MonthlyResult, ConsumptionPattern } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
  isG12DayHour,
  isG12DayHourForOSD,
} from './constants';
import { roundToTwoDecimals, distributeConsumption } from './helpers';

/**
 * Calculate G12 (day/night) tariff cost
 *
 * Formula:
 * - Split consumption by time: day (06:00-22:00), night (22:00-06:00)
 * - Energy cost = (dayKwh × dayPrice) + (nightKwh × nightPrice) + qualityCharge
 * - Distribution cost = fixedFee + (dayKwh × dayDistribution) + (nightKwh × nightDistribution)
 * - Total (gross) = (Energy + Distribution) × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G12 pricing data
 * @param consumptionPattern - Hourly pattern (weekday/weekend)
 * @param daysInMonth - Number of days in month
 * @param year - Year (for holiday detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Monthly calculation result with day/night split
 *
 * @example
 * const result = calculateG12(300, TAURON_2025_PRICING.g12, UNIFORM_PATTERN, 31, 2025, 0);
 * // Returns: { totalKwh: 300, dayKwh: 200, nightKwh: 100, productionCost: 142.5, distributionCost: 72.1, totalCost: 263.87 }
 */
export function calculateG12(
  monthlyConsumption: number,
  pricing: G12Pricing,
  consumptionPattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number,
  osd?: string
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }
  if (!consumptionPattern) {
    throw new Error('Consumption pattern is required');
  }

  // Distribute consumption across hours
  const hourlyConsumption = distributeConsumption(
    monthlyConsumption,
    consumptionPattern,
    daysInMonth,
    year,
    month
  );

  // Split into day and night consumption
  let dayKwh = 0;
  let nightKwh = 0;

  for (const hourData of hourlyConsumption) {
    if (osd ? isG12DayHourForOSD(hourData.hour, osd, month) : isG12DayHour(hourData.hour)) {
      dayKwh += hourData.consumption;
    } else {
      nightKwh += hourData.consumption;
    }
  }

  // Calculate energy production cost
  const energyDayCost = dayKwh * pricing.energyDayPricePerKwh;
  const energyNightCost = nightKwh * pricing.energyNightPricePerKwh;
  const qualityCost = monthlyConsumption * (pricing.qualityCharge || 0);
  const productionCost = energyDayCost + energyNightCost + qualityCost;

  // Calculate distribution cost
  const distributionDayCost = dayKwh * pricing.distributionDayPerKwh;
  const distributionNightCost = nightKwh * pricing.distributionNightPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionDayCost + distributionNightCost;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: roundToTwoDecimals(monthlyConsumption),
    dayKwh: roundToTwoDecimals(dayKwh),
    nightKwh: roundToTwoDecimals(nightKwh),
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
